import { useQuery } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { useAuthenticatedUser, useIsSupplierRole } from '@omnea-app/fe-auth/UserSessionProvider'
import { useDebouncedValue } from '@omnea-app/fe-timing/useDebouncedValue'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { CUSTOM_SUPPLIER_ID } from '@omnea-app/products/productsFrontendClient'
import { ProductsHttpClient } from '@omnea-app/products/productsHttpClient'
import { SupplierOptionType, type ProductOptionType, type NewSupplier } from '../types'
import { sortOptionsByType } from '../utils'

// The most the search returns in one call, so a longer narrowed list arrives cut off at this length.
export const FIELD_FILTERED_LIMIT = 50

export const createNewSupplierOption = (name: string): NewSupplier => {
  return { id: CUSTOM_SUPPLIER_ID, name, type: 'NEW_SUPPLIER', isPreferred: false, logoUrl: null }
}

/**
 * Returns an array of options or a single new supplier option if there are no options available.
 * The ListBoxWithAddNewOption component handles rendering of the "Add new supplier" option when there are options returned from the API.
 **/
const getOptions = (
  searchTerm: string,
  data: { suppliers: SupplierOptionType[]; products: ProductOptionType[] } | undefined,
  allowNewSupplier: boolean
): (ProductOptionType | SupplierOptionType)[] | [NewSupplier] => {
  const suppliers = data?.suppliers ?? []
  const products = data?.products ?? []
  const options: (ProductOptionType | SupplierOptionType)[] = [...suppliers, ...products]

  // Only show new supplier option if we have a search term and no matches
  if (allowNewSupplier && options.length === 0 && searchTerm.length > 0) {
    return [createNewSupplierOption(searchTerm)]
  }

  return options.sort(sortOptionsByType)
}

const SearchSuppliersAndProductsQueryKey = (
  name: string,
  includeGlobal: boolean,
  preferredOnly: boolean,
  limit?: number,
  fieldOptions?: string
) => ['SearchSuppliersAndProductsQueryKey', name, includeGlobal, preferredOnly, limit, fieldOptions]

const useQueryFn = ({
  name,
  includeGlobal,
  limit,
  includeSupplierTags,
  preferredOnly,
  fieldOptions,
}: {
  name: string
  includeGlobal: boolean
  limit?: number
  includeSupplierTags?: boolean
  preferredOnly: boolean
  fieldOptions?: string
}) => {
  const { getToken } = useAuthenticatedUser()

  return async ({ signal }: { signal?: AbortSignal }) => {
    const token = await getToken()
    const apiClient = new ProductsHttpClient({ authorization: token })
    const { data, error } = await apiClient.GET('/search', {
      params: {
        query: {
          name,
          includeGlobal: includeGlobal ? 'true' : 'false',
          includeSupplierTags: includeSupplierTags ? 'true' : 'false',
          preferredOnly: preferredOnly ? 'true' : 'false',
          limit: limit !== undefined ? limit.toString() : undefined,
          fieldOptions,
        },
      },
      signal,
    })
    throwIfApiError(error)

    const products = data.products ?? []
    const suppliers = data.suppliers ?? []

    return {
      products,
      suppliers,
      appliedFieldIds: data.appliedFieldIds ?? [],
    }
  }
}

export const useSuppliersAndProducts = ({
  name,
  includeGlobal,
  limit,
  enableOverride,
  includeSupplierTags,
  preferredOnly = false,
  fieldOptions,
}: {
  name: string
  includeGlobal: boolean
  limit?: number
  enableOverride?: boolean
  includeSupplierTags?: boolean
  preferredOnly?: boolean
  fieldOptions?: string
}) => {
  const isSupplier = useIsSupplierRole()
  const debouncedSearchTerm = useDebouncedValue(name, { delay: 150 })
  // Suppliers are excluded from searchProducts (it returns the buyer org's catalogue), so the call
  // always 403s and yields no options. Never fire it for them. See BUY-1209.
  const enabled = !isSupplier && ((debouncedSearchTerm.length > 0 || enableOverride) ?? false)
  return useQuery({
    queryKey: SearchSuppliersAndProductsQueryKey(debouncedSearchTerm, includeGlobal, preferredOnly, limit, fieldOptions),
    queryFn: useQueryFn({ name: debouncedSearchTerm, includeGlobal, limit, includeSupplierTags, preferredOnly, fieldOptions }),
    enabled,
    // The endpoint will be invoked many times whilst a user searches. We only care about the current query result
    // The lines below ensure the the stale HTTP calls are aborted
    staleTime: 0,
    gcTime: 0,
  })
}

export const useSuppliersAndProductsSearch = ({
  includeGlobal,
  limit,
  enableOverride,
  includeSupplierTags,
  preferredOnly = false,
  allowNewSupplier = true,
  fieldOptions,
}: {
  includeGlobal: boolean
  limit?: number
  enableOverride?: boolean
  includeSupplierTags?: boolean
  preferredOnly?: boolean
  allowNewSupplier?: boolean
  fieldOptions?: string
}) => {
  const [searchTerm, setSearchTerm] = useState('%')
  const [options, setOptions] = useState<(ProductOptionType | SupplierOptionType)[] | [NewSupplier]>([])
  const [isManualLoading, setIsManualLoading] = useState(false)

  const { data, isLoading: isSearchLoading } = useSuppliersAndProducts({
    // A narrowed list is offered whole before anything is typed.
    name: fieldOptions && !searchTerm ? '%' : searchTerm,
    includeGlobal,
    limit,
    enableOverride,
    includeSupplierTags,
    preferredOnly,
    fieldOptions,
  })

  const optionsFieldOptions = useRef(fieldOptions)

  const search = useCallback(
    async (term: string) => {
      const trimmedTerm = term.trim()
      setSearchTerm(trimmedTerm)
      setIsManualLoading(true)

      if (!trimmedTerm && !fieldOptions) {
        setOptions(getOptions(trimmedTerm, { suppliers: [], products: [] }, allowNewSupplier))
      }

      if (data) {
        setOptions(getOptions(trimmedTerm, data, allowNewSupplier))
        optionsFieldOptions.current = fieldOptions
      } else if (optionsFieldOptions.current !== fieldOptions) {
        // Suppliers listed for the previous answers must not stay selectable while the new list loads.
        setOptions([])
      }
      setIsManualLoading(false)
    },
    [data, allowNewSupplier, fieldOptions]
  )

  const isLoading = isManualLoading || isSearchLoading

  return {
    isLoading,
    search,
    options,
  }
}
